<template>

    <div v-if="$can('read','reimbursement')" class="reimbursement-list-page report-tracker-page">

        <div class="reimbursement-list-content">

            <!-- Pill tab navigation -->
            <div class="reimbursement-view-tabs" role="tablist" aria-label="Reimbursement views">
                <button
                    type="button"
                    role="tab"
                    class="reimbursement-tab-pill"
                    :class="{ 'reimbursement-tab-pill--active': viewMode === 'mine' }"
                    :aria-selected="viewMode === 'mine' ? 'true' : 'false'"
                    @click="switchView('mine')"
                >
                    <feather-icon icon="UserIcon" size="16" />
                    <span>My Reimbursements</span>
                </button>
                <button
                    v-if="showApprovalsTab"
                    type="button"
                    role="tab"
                    class="reimbursement-tab-pill"
                    :class="{ 'reimbursement-tab-pill--active': viewMode === 'approvals' }"
                    :aria-selected="viewMode === 'approvals' ? 'true' : 'false'"
                    @click="switchView('approvals')"
                >
                    <feather-icon icon="CheckCircleIcon" size="16" />
                    <span>Approvals</span>
                    <b-badge v-if="approvalQueueCount" variant="warning" pill class="reimbursement-tab-badge">{{ approvalQueueCount }}</b-badge>
                </button>
                <button
                    v-if="showAllTab"
                    type="button"
                    role="tab"
                    class="reimbursement-tab-pill"
                    :class="{ 'reimbursement-tab-pill--active': viewMode === 'all' }"
                    :aria-selected="viewMode === 'all' ? 'true' : 'false'"
                    @click="switchView('all')"
                >
                    <feather-icon icon="GlobeIcon" size="16" />
                    <span>All Reimbursements</span>
                </button>
            </div>

            <!-- Compact toolbar -->
            <div class="report-toolbar">
                <div
                    class="report-toolbar-body"
                    :class="{ 'report-toolbar-body--filters-open': showFilters }"
                >
                    <div class="report-toolbar-row report-toolbar-row--compact">
                        <button
                            type="button"
                            class="report-filter-toggle reimb-filter-toggle"
                            :class="{ 'report-filter-toggle--active': showFilters || hasActiveFilters }"
                            :aria-expanded="showFilters ? 'true' : 'false'"
                            aria-controls="reimb-filter-panel"
                            aria-label="Filters"
                            @click="showFilters = !showFilters"
                        >
                            <feather-icon icon="FilterIcon" size="16" />
                            <span
                                v-if="hasActiveFilters"
                                class="report-filter-badge"
                            >
                                {{ activeFilterCount }}
                            </span>
                        </button>

                        <div class="report-toolbar-context reimbursement-toolbar-context">
                            <feather-icon icon="ListIcon" size="16" class="report-context-icon" />
                            <span class="report-context-text">
                                <strong>{{ totalRows }}</strong>
                                {{ totalRows === 1 ? 'record' : 'records' }}
                            </span>
                        </div>

                        <div class="report-toolbar-actions">
                            <div class="reimbursement-toolbar-search">
                                <b-input-group size="sm" class="reimbursement-search-group">
                                    <b-input-group-prepend is-text>
                                        <feather-icon icon="SearchIcon" size="14" />
                                    </b-input-group-prepend>
                                    <b-form-input
                                        id="reimb-toolbar-search"
                                        v-model="filter"
                                        type="search"
                                        placeholder="Search…"
                                        autocomplete="off"
                                    />
                                </b-input-group>
                            </div>
                            <button
                                v-if="canDownload"
                                type="button"
                                class="reimb-icon-btn"
                                aria-label="Download CSV"
                                :disabled="loading || downloadInProgress"
                                @click="downloadReimbursementsCsv"
                            >
                                <feather-icon icon="DownloadIcon" size="16" />
                            </button>
                            <router-link
                                v-if="$can('create','reimbursement')"
                                :to="{ name: 'apps-add-reimbursement' }"
                                class="reimbursement-raise-link"
                            >
                                <button
                                    type="button"
                                    class="reimb-icon-btn"
                                    aria-label="Raise Reimbursement"
                                >
                                    <feather-icon icon="PlusIcon" size="16" />
                                </button>
                            </router-link>
                        </div>
                    </div>
                </div>

                <b-collapse id="reimb-filter-panel" v-model="showFilters">
                    <div class="report-filter-panel">
                        <h6 class="report-section-heading">Reimbursement cycle</h6>
                        <div class="report-filter-grid reimbursement-filter-grid">
                            <div class="report-filter-group report-filter-group--date">
                                <label class="report-filter-label" for="reimb-cycle-start">Cycle start</label>
                                <flat-pickr
                                    id="reimb-cycle-start"
                                    v-model="searchFilters.startDate"
                                    class="form-control form-control-sm report-date-input"
                                    placeholder="YYYY-MM-DD"
                                    :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                                />
                            </div>
                            <div class="report-filter-group report-filter-group--date">
                                <label class="report-filter-label" for="reimb-cycle-end">Cycle end</label>
                                <flat-pickr
                                    id="reimb-cycle-end"
                                    v-model="searchFilters.endDate"
                                    class="form-control form-control-sm report-date-input"
                                    placeholder="YYYY-MM-DD"
                                    :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                                />
                            </div>
                            <div class="report-filter-group reimbursement-filter-actions">
                                <label class="report-filter-label d-none d-md-block">&nbsp;</label>
                                <div class="d-flex flex-wrap reimbursement-filter-action-btns">
                                    <b-button size="sm" variant="primary" @click="applyDateFilter">Apply cycle</b-button>
                                    <b-button size="sm" variant="outline-secondary" @click="resetDateFilter">Reset cycle</b-button>
                                </div>
                            </div>
                        </div>

                        <hr class="report-filter-panel-divider" />

                        <h6 class="report-section-heading">Table options</h6>
                        <div class="report-filter-grid reimbursement-filter-grid">
                            <div class="report-filter-group">
                                <label class="report-filter-label" for="perPageSelect">Per page</label>
                                <b-form-select
                                    id="perPageSelect"
                                    v-model="perPage"
                                    size="sm"
                                    :options="pageOptions"
                                />
                            </div>
                        </div>
                    </div>
                </b-collapse>
            </div>

            <div class="report-table-wrap reimbursement-table-wrap">
            <b-table

                hover

                responsive

                stacked="md"

                class="report-table reimbursement-table mb-0"

                :per-page="perPage"

                :current-page="currentPage"

                :items="displayItems"

                :fields="tableFields"

                :filter="filter"

                :filter-included-fields="filterOn"

                @filtered="onFiltered"

                show-empty

                :busy="loading"

            >

                <template #table-busy>

                    <div class="text-center text-muted py-2">Loading...</div>

                </template>

                <template #cell(submitted_by)="data">

                    <div class="text-nowrap">{{ submittedByName(data.item) }}</div>

                </template>

                <template #cell(employee)="data">

                    <div class="text-nowrap">{{ employeeName(data.item) }}</div>

                </template>

                <template #cell(expense_date)="data">

                    <div class="text-nowrap">{{ data.item.expense_date }}</div>

                </template>

                <template #cell(item)="data">

                    <div
                        class="reimb-item-cell text-truncate"
                        :title="data.item.item || data.item.comment || ''"
                    >
                        {{ data.item.item || data.item.comment || '—' }}
                    </div>

                    <div
                        v-if="viewMode === 'mine' && onBehalfName(data.item)"
                        class="text-nowrap reimb-on-behalf"
                    >
                        On behalf of: {{ onBehalfName(data.item) }}
                    </div>

                </template>

                <template #cell(account)="data">

                    <div class="text-nowrap">{{ data.item.account || '—' }}</div>

                </template>

                <template #cell(category)="data">

                    <div class="text-nowrap">{{ data.item.category }}</div>

                    <div class="text-nowrap reimb-subcategory" v-if="data.item.sub_category">{{ data.item.sub_category }}</div>

                </template>

                <template #cell(amount)="data">

                    <div class="text-nowrap">{{ data.item.amount }}</div>

                </template>

                <template #cell(status)="data">

                    <b-badge :variant="statusVariant(data.item)">{{ statusLabel(data.item) }}</b-badge>

                </template>

                <template #cell(actions)="data">

                    <b-dropdown
                        variant="link"
                        toggle-class="text-decoration-none p-0 reimb-actions-toggle"
                        no-caret
                        right
                        boundary="window"
                        :popper-opts="{ positionFixed: true }"
                        class="reimbursement-actions-dropdown"
                    >
                        <template #button-content>
                            <feather-icon icon="MoreVerticalIcon" size="16" class="text-body" />
                        </template>

                        <b-dropdown-item @click="viewDetails(data.item)">
                            <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                            View
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="itemFilePath(data.item)"
                            @click="openItemAttachment(data.item)"
                        >
                            <feather-icon icon="PaperclipIcon" size="14" class="mr-50" />
                            View document
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="canEditItem(data.item)"
                            @click="editItem(data.item)"
                        >
                            <feather-icon icon="EditIcon" size="14" class="mr-50" />
                            Edit
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="canCancelItem(data.item)"
                            variant="danger"
                            :disabled="actionInProgress === data.item.id"
                            @click="confirmCancel(data.item)"
                        >
                            <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
                            Withdraw
                        </b-dropdown-item>

                        <template v-if="canActOnItem(data.item)">
                            <b-dropdown-divider />
                            <b-dropdown-item
                                :disabled="actionInProgress === data.item.id"
                                @click="confirmApprove(data.item)"
                            >
                                <feather-icon icon="CheckIcon" size="14" class="mr-50" />
                                Approve
                            </b-dropdown-item>
                            <b-dropdown-item
                                variant="danger"
                                :disabled="actionInProgress === data.item.id"
                                @click="openRejectModal(data.item)"
                            >
                                <feather-icon icon="XIcon" size="14" class="mr-50" />
                                Reject
                            </b-dropdown-item>
                        </template>
                    </b-dropdown>

                </template>

                <template #empty>

                    <div class="report-empty-state reimbursement-empty-state">
                        <feather-icon
                            :icon="emptyStateIcon"
                            size="48"
                            class="report-empty-icon"
                        />
                        <p class="report-empty-title">{{ emptyMessage }}</p>
                        <p v-if="viewMode === 'mine'" class="report-empty-hint">
                            Submit an expense claim with receipts to start tracking reimbursements.
                        </p>
                        <p v-else-if="viewMode === 'approvals'" class="report-empty-hint">
                            Claims awaiting your approval will appear here.
                        </p>
                        <p v-else class="report-empty-hint">
                            Try adjusting the cycle dates or search filters above.
                        </p>
                        <router-link
                            v-if="viewMode === 'mine' && $can('create','reimbursement')"
                            :to="{ name: 'apps-add-reimbursement' }"
                            class="mt-75"
                        >
                            <b-button variant="primary" size="sm">
                                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                                Raise Reimbursement
                            </b-button>
                        </router-link>
                    </div>

                </template>

            </b-table>
            </div>



            <div class="report-toolbar-row report-toolbar-row--footer reimbursement-pagination-footer">
                <span class="reimbursement-page-summary">
                    Showing page {{ currentPage }} of {{ totalPages }}
                </span>
                <b-pagination

                    v-model="currentPage"

                    :total-rows="totalRows"

                    :per-page="perPage"

                    align="right"

                    class="mb-0 reimbursement-pagination"

                />
            </div>

        </div>



        <!-- View Details -->

        <b-modal

            id="reimbursement-view-details-modal"

            ref="viewDetailsModal"

            title="Reimbursement Details"

            size="lg"

            ok-only

            ok-title="Close"

        >

            <div v-if="selectedItem">

                <b-row>

                    <b-col cols="12" md="6"><strong>Item / Title:</strong> {{ selectedItem.item || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1 mt-md-0"><strong>Account / Client:</strong> {{ selectedItem.account || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Vertical:</strong> {{ selectedItem.vertical || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Category:</strong> {{ selectedItem.category || 'Indirect' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Sub-Category:</strong> {{ selectedItem.sub_category || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Payment Mode:</strong> {{ selectedItem.payment_mode || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Amount:</strong> {{ selectedItem.amount }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Date:</strong> {{ selectedItem.expense_date }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Invoice No:</strong> {{ selectedItem.invoice_no || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Location:</strong> {{ selectedItem.location || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1">

                        <strong>GST:</strong> {{ selectedItem.is_gst ? 'Yes' : 'No' }}

                        <span v-if="selectedItem.is_gst && (selectedItem.gst_number || selectedItem.gstNumber)">
                            ({{ selectedItem.gst_number || selectedItem.gstNumber }})
                        </span>

                    </b-col>

                    <b-col cols="12" md="6" class="mt-1">

                        <strong>TDS:</strong> {{ selectedItem.is_tds ? 'Yes' : 'No' }}

                        <span v-if="selectedItem.is_tds"> (Amount: {{ selectedItem.tds_amount }})</span>

                    </b-col>

                    <b-col cols="12" class="mt-1"><strong>Comment:</strong> {{ selectedItem.comment || '-' }}</b-col>

                    <b-col cols="12" class="mt-1">

                        <strong>Supporting Documents:</strong>

                        <template v-if="selectedItemFilePath">
                            <a href="#" @click.prevent="openAttachment">
                                View document
                            </a>
                            <span v-if="selectedItemFileName" class="text-muted small ml-50">
                                ({{ selectedItemFileName }})
                            </span>
                        </template>

                        <span v-else>-</span>

                    </b-col>

                    <b-col cols="12" md="6" class="mt-1">

                        <strong>Submitted By:</strong> {{ submittedByName(selectedItem) }}

                    </b-col>

                    <b-col cols="12" md="6" class="mt-1">

                        <strong>Employee:</strong> {{ employeeName(selectedItem) }}

                        <div v-if="onBehalfName(selectedItem)" class="text-muted">On behalf of: {{ onBehalfName(selectedItem) }}</div>

                    </b-col>

                    <b-col cols="12" md="6" class="mt-1">

                        <strong>Status:</strong> <b-badge :variant="statusVariant(selectedItem)">{{ statusLabel(selectedItem) }}</b-badge>

                    </b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Created At:</strong> {{ selectedItem.createdAt || '-' }}</b-col>

                    <b-col cols="12" md="6" class="mt-1"><strong>Updated At:</strong> {{ selectedItem.updatedAt || '-' }}</b-col>

                </b-row>



                <hr class="my-2" />

                <h6 class="mb-1">Approval Trail</h6>

                <b-row>

                    <b-col cols="12" class="mb-1">

                        <strong>{{ trailLabel(selectedItem, REIMBURSEMENT_STATUSES.PENDING_L1) }}</strong>

                        <span v-if="selectedItem.level1_approved_by">

                            by {{ parsedName(selectedItem.level1_approved_by) }}

                            on {{ formatDateTime(selectedItem.level1_approved_at) }}

                            <span v-if="selectedItem.level1_comment" class="text-muted"> — {{ selectedItem.level1_comment }}</span>

                        </span>

                    </b-col>

                    <b-col cols="12" class="mb-1">

                        <strong>{{ trailLabel(selectedItem, REIMBURSEMENT_STATUSES.PENDING_L2) }}</strong>

                        <span v-if="selectedItem.level2_approved_by">

                            by {{ parsedName(selectedItem.level2_approved_by) }}

                            on {{ formatDateTime(selectedItem.level2_approved_at) }}

                            <span v-if="selectedItem.level2_comment" class="text-muted"> — {{ selectedItem.level2_comment }}</span>

                        </span>

                    </b-col>

                    <b-col cols="12" class="mb-1">

                        <strong>{{ trailLabel(selectedItem, REIMBURSEMENT_STATUSES.PENDING_FINAL) }}</strong>

                        <span v-if="selectedItem.final_approved_by">

                            by {{ parsedName(selectedItem.final_approved_by) }}

                            on {{ formatDateTime(selectedItem.final_approved_at) }}

                            <span v-if="selectedItem.final_comment" class="text-muted"> — {{ selectedItem.final_comment }}</span>

                        </span>

                    </b-col>

                    <b-col cols="12" v-if="selectedItem.rejected_by" class="text-danger">

                        <strong>{{ rejectedStageLabel(selectedItem) }}</strong>

                        by {{ parsedName(selectedItem.rejected_by) }}

                        on {{ formatDateTime(selectedItem.rejected_at) }}

                        <span v-if="selectedItem.rejection_comment"> — {{ selectedItem.rejection_comment }}</span>

                    </b-col>

                </b-row>



                <div v-if="canActOnItem(selectedItem)" class="mt-2 d-flex" style="gap: 8px;">

                    <b-button
                        variant="success"
                        class="reimbursement-icon-btn"
                        v-b-tooltip.hover
                        title="Approve"
                        :disabled="actionInProgress === selectedItem.id"
                        @click="confirmApprove(selectedItem)"
                    >
                        <feather-icon icon="CheckIcon" size="16" />
                    </b-button>

                    <b-button
                        variant="outline-danger"
                        class="reimbursement-icon-btn"
                        v-b-tooltip.hover
                        title="Reject"
                        :disabled="actionInProgress === selectedItem.id"
                        @click="openRejectModal(selectedItem)"
                    >
                        <feather-icon icon="XIcon" size="16" />
                    </b-button>

                </div>

                <div v-else-if="canEditItem(selectedItem) || canCancelItem(selectedItem)" class="mt-2 d-flex" style="gap: 8px;">

                    <b-button
                        v-if="canEditItem(selectedItem)"
                        variant="outline-info"
                        class="reimbursement-icon-btn"
                        v-b-tooltip.hover
                        title="Edit"
                        @click="editItem(selectedItem)"
                    >
                        <feather-icon icon="EditIcon" size="16" />
                    </b-button>

                    <b-button
                        v-if="canCancelItem(selectedItem)"
                        variant="outline-warning"
                        class="reimbursement-icon-btn"
                        v-b-tooltip.hover
                        title="Withdraw"
                        :disabled="actionInProgress === selectedItem.id"
                        @click="confirmCancel(selectedItem)"
                    >
                        <feather-icon icon="Trash2Icon" size="16" />
                    </b-button>

                </div>

            </div>

        </b-modal>



        <b-modal

            id="reimbursement-cancel-modal"

            ref="cancelModal"

            title="Withdraw Reimbursement"

            ok-title="Withdraw"

            ok-variant="warning"

            cancel-title="Keep"

            @ok="submitCancel"

        >

            <p v-if="cancelTarget" class="mb-0">

                Withdraw claim <strong>{{ cancelTarget.item || cancelTarget.category }}</strong>

                ({{ statusLabel(cancelTarget) }})? This cannot be undone.

            </p>

        </b-modal>



        <b-modal

            id="reimbursement-reject-modal"

            ref="rejectModal"

            title="Reject Reimbursement"

            ok-title="Reject"

            ok-variant="danger"

            cancel-title="Cancel"

            @ok="submitReject"

        >

            <p class="mb-1" v-if="rejectTarget">

                Reject claim <strong>{{ rejectTarget.item }}</strong> from {{ claimPartyLabel(rejectTarget) }}?

            </p>

            <b-form-group
                label-for="reimbursement-reject-comment"
                :state="rejectCommentError ? false : null"
                invalid-feedback="Comment is required"
            >
                <template #label>
                    Comment
                    <span class="text-danger">*</span>
                </template>
                <b-form-textarea
                    id="reimbursement-reject-comment"
                    v-model="rejectComment"
                    rows="3"
                    placeholder="Reason for rejection"
                    :state="rejectCommentError ? false : null"
                    @input="rejectCommentError = false"
                />
            </b-form-group>

        </b-modal>



        <b-modal

            id="reimbursement-approve-modal"

            ref="approveModal"

            title="Approve Reimbursement"

            ok-title="Approve"

            ok-variant="success"

            cancel-title="Cancel"

            @ok="submitApprove"

        >

            <p v-if="approveTarget" class="mb-1">

                Approve claim <strong>{{ approveTarget.item }}</strong> from {{ claimPartyLabel(approveTarget) }}?

            </p>

            <b-form-group
                label-for="reimbursement-approve-comment"
                :state="approveCommentError ? false : null"
                invalid-feedback="Comment is required"
            >
                <template #label>
                    Comment
                    <span class="text-danger">*</span>
                </template>
                <b-form-textarea
                    id="reimbursement-approve-comment"
                    v-model="approveComment"
                    rows="2"
                    placeholder="Add an approval comment"
                    :state="approveCommentError ? false : null"
                    @input="approveCommentError = false"
                />
            </b-form-group>

        </b-modal>

        <b-modal
            id="reimbursement-doc-preview-modal"
            ref="docPreviewModal"
            :title="docPreviewTitle"
            size="xl"
            centered
            @hidden="resetDocPreview"
        >
            <div v-if="docPreviewLoading" class="text-center text-muted py-5">
                Loading document…
            </div>
            <div v-else-if="docPreviewKind === 'pdf' && docPreviewSrc">
                <iframe
                    :src="docPreviewSrc"
                    frameborder="0"
                    width="100%"
                    title="Document preview"
                    class="reimbursement-doc-preview-frame"
                />
            </div>
            <div v-else-if="docPreviewKind === 'image' && docPreviewSrc" class="text-center">
                <img
                    :src="docPreviewSrc"
                    alt="Document preview"
                    class="reimbursement-doc-preview-image"
                />
            </div>
            <div v-else class="text-center text-muted py-3">
                Preview is not available for this file type.
            </div>
            <template #modal-footer="{ hide }">
                <b-button
                    variant="outline-secondary"
                    :disabled="docPreviewLoading || (!docPreviewDownloadUrl && !docPreviewSrc)"
                    @click="downloadDocPreview"
                >
                    <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
                    Download
                </b-button>
                <b-button variant="primary" @click="hide()">Close</b-button>
            </template>
        </b-modal>

    </div>

</template>

<script>

import {

    BRow, BCol, BButton, BTable, BBadge, BPagination,

    BInputGroup, BFormInput, BFormGroup, BInputGroupPrepend, BFormSelect, BModal,

    BFormTextarea, BAlert, BCollapse, BDropdown, BDropdownItem, BDropdownDivider, VBTooltip,

} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import reimbursementService, {
    extractReimbursementRow,
    formatReimbursementApiError,
} from '@/services/reimbursement-service'

import JobService from '@/services/job-service'

import { deriveReimbursementStatus, deriveReimbursementStatusVariant, getDefaultReimbursementCycleDates, REIMBURSEMENT_DATE_FILTER_FIELD, getReimbursementFilePath, getReimbursementFileName } from '@/utils/reimbursement-config'

import flatPickr from 'vue-flatpickr-component'

import {

    canApproveAtStage,

    canCancelOwnReimbursement,

    canDownloadReimbursements,

    canEditReimbursement,

    canViewAllReimbursements,

    canViewApprovalsTab,

    filterApprovalQueue,

    getApproveDenialReason,

    getCancelDenialReason,

    getEditDenialReason,

    isOwnSubmission,

    getReimbursementOnBehalfName,

    getReimbursementSubmitterName,

    getReimbursementEmployeeName,

    NEXT_STATUS_AFTER_APPROVAL,

    normalizeReimbursementStatus,

    REIMBURSEMENT_STATUSES,

    formatReimbursementTrailLabel,

    formatReimbursementStageOutcome,

    formatReimbursementStatusLabel,

} from '@/utils/reimbursement-permissions'



export default {

    components: {

        BRow, BCol, BButton, BTable, BBadge, BPagination,

        BInputGroup, BFormInput, BInputGroupPrepend, BFormSelect, BFormGroup, BModal,

        BFormTextarea, BAlert, BCollapse, BDropdown, BDropdownItem, BDropdownDivider,

        flatPickr,

    },

    directives: {
        'b-tooltip': VBTooltip,
    },

    data() {

        const defaultCycle = getDefaultReimbursementCycleDates()

        return {

            viewMode: 'all',

            showFilters: false,

            REIMBURSEMENT_STATUSES,

            defaultPerPage: 20,

            defaultCycleDates: { ...defaultCycle },

            loading: false,

            downloadInProgress: false,

            actionInProgress: null,

            pageOptions: [10, 20, 30, 50, 100],

            perPage: 20,

            currentPage: 1,

            totalRows: 0,

            filter: '',

            reimbursementList: [],

            approvalQueueList: [],

            selectedItem: null,

            approveTarget: null,

            approveComment: '',

            approveCommentError: false,

            rejectTarget: null,

            rejectComment: '',

            rejectCommentError: false,

            cancelTarget: null,

            userData: {},

            docPreviewLoading: false,

            docPreviewKind: null,

            docPreviewSrc: null,

            docPreviewDownloadUrl: null,

            docPreviewTitle: 'Document preview',

            docPreviewFileName: null,

            baseFields: [

                { key: 'expense_date', label: 'Date', sortable: true },

                { key: 'item', label: 'Item / Title', sortable: true },

                { key: 'account', label: 'Account', sortable: true },

                { key: 'category', label: 'Category', sortable: true },

                { key: 'amount', label: 'Amount', sortable: true, tdClass: 'numeric-col', thClass: 'numeric-col' },

                { key: 'status', label: 'Status', sortable: true },

                { key: 'actions', label: 'Actions', tdClass: 'text-nowrap', thClass: 'text-nowrap' },

            ],

            filterOn: ['item', 'account', 'category', 'sub_category', 'amount', 'expense_date', 'status', 'status_label', 'submitted_by_search', 'employee', 'employee_display'],

            searchFilters: {

                dateFieldSelected: REIMBURSEMENT_DATE_FILTER_FIELD,

                startDate: defaultCycle.startDate,

                endDate: defaultCycle.endDate,

            },

        }

    },

    computed: {

        showApprovalsTab() {

            return canViewApprovalsTab(this.$can.bind(this), this.userData)

        },

        showAllTab() {

            return canViewAllReimbursements(this.$can.bind(this), this.userData)

        },

        canDownload() {

            return canDownloadReimbursements(this.$can.bind(this))

        },

        hasActiveFilters() {

            return this.activeFilterCount > 0

        },

        activeFilterCount() {

            let count = 0

            if (this.perPage !== this.defaultPerPage) count += 1

            if (
                this.searchFilters.startDate !== this.defaultCycleDates.startDate
                || this.searchFilters.endDate !== this.defaultCycleDates.endDate
            ) {
                count += 1
            }

            return count

        },

        totalPages() {

            if (!this.totalRows || !this.perPage) return 1

            return Math.max(1, Math.ceil(this.totalRows / this.perPage))

        },

        emptyStateIcon() {

            if (this.viewMode === 'approvals') return 'CheckCircleIcon'

            if (this.viewMode === 'all') return 'SearchIcon'

            return 'InboxIcon'

        },

        tableFields() {

            return [

                { key: 'submitted_by', label: 'Submitted By', sortable: true },

                { key: 'employee', label: 'Employee', sortable: true },

                ...this.baseFields,

            ]

        },

        displayItems() {

            if (this.viewMode === 'mine') {

                return this.reimbursementList.filter(item => isOwnSubmission(this.userData, item))

            }

            if (this.viewMode === 'approvals') {

                return this.reimbursementList

            }

            return this.reimbursementList

        },

        approvalQueueCount() {

            return this.approvalQueueList.length

        },

        emptyMessage() {

            if (this.viewMode === 'approvals') return 'No reimbursements pending your approval.'

            if (this.viewMode === 'all') return 'No reimbursements found.'

            return 'No reimbursements raised yet.'

        },

        selectedItemFilePath() {
            return getReimbursementFilePath(this.selectedItem)
        },

        selectedItemFileName() {
            return getReimbursementFileName(this.selectedItemFilePath)
        },

    },

    watch: {
        '$route.query.tab'() {
            const resolved = this.resolveInitialViewMode()
            if (resolved === this.viewMode) return
            this.viewMode = resolved
            this.filter = ''
            this.currentPage = 1
            this.loadList()
        },
    },

    created() {

        this.userData = JSON.parse(localStorage.getItem('userData')) || {}

        if (!this.$can('read', 'reimbursement')) {

            this.$router.push({ name: 'misc-not-authorized' })

            return

        }

        this.viewMode = this.resolveInitialViewMode()

    },

    mounted() {

        this.loadList()

    },

    methods: {

        itemFilePath(row) {
            return getReimbursementFilePath(row)
        },

        async openItemAttachment(item) {
            await this.previewAttachment(
                getReimbursementFilePath(item),
                getReimbursementFileName(getReimbursementFilePath(item)),
            )
        },

        async openAttachment() {
            await this.previewAttachment(this.selectedItemFilePath, this.selectedItemFileName)
        },

        attachmentExtension(filePath) {
            if (!filePath) return ''
            const clean = String(filePath).split('?')[0].split('#')[0]
            const segment = clean.split('/').filter(Boolean).pop() || clean
            const parts = segment.split('.')
            if (parts.length < 2) return ''
            return parts.pop().toLowerCase()
        },

        imageMimeForExtension(ext) {
            if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
            if (ext === 'png') return 'image/png'
            if (ext === 'gif') return 'image/gif'
            if (ext === 'webp') return 'image/webp'
            if (ext === 'bmp') return 'image/bmp'
            return `image/${ext}`
        },

        async previewAttachment(filePath, fileName) {
            if (!filePath) return

            const ext = this.attachmentExtension(filePath)
            const isPdf = ext === 'pdf'
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)

            if (!isPdf && !isImage) {
                const url = await reimbursementService.resolveAttachmentUrl(filePath)
                if (url) {
                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Preview not available',
                            text: 'This file type cannot be previewed in ATS. Opening in a new tab / download instead.',
                            icon: 'InfoIcon',
                            variant: 'info',
                        },
                    })
                    window.open(url, '_blank', 'noopener')
                } else {
                    this.showAttachmentUnavailableToast()
                }
                return
            }

            this.docPreviewLoading = true
            this.docPreviewKind = isPdf ? 'pdf' : 'image'
            this.docPreviewSrc = null
            this.docPreviewDownloadUrl = null
            this.docPreviewFileName = fileName || getReimbursementFileName(filePath) || 'document'
            this.docPreviewTitle = this.docPreviewFileName
            this.$refs.docPreviewModal.show()

            try {
                const resolvedUrl = await reimbursementService.resolveAttachmentUrl(filePath)
                this.docPreviewDownloadUrl = resolvedUrl

                // Direct http(s) / already-absolute URLs: embed when possible
                if (/^https?:\/\//i.test(String(filePath).trim())) {
                    this.docPreviewSrc = String(filePath).trim()
                    this.docPreviewDownloadUrl = this.docPreviewSrc
                    return
                }

                // Prefer base64 data URL (same pattern as resume / invoice PDF preview)
                // so Content-Disposition: attachment does not force a download.
                const key = String(filePath).trim().replace(/^\//, '').replace('/', '%2F')
                const res = await JobService.getFileBase64(key)
                const base64 = res && res.file
                if (base64 && typeof base64 === 'string' && !base64.startsWith('http')) {
                    const mime = isPdf ? 'application/pdf' : this.imageMimeForExtension(ext)
                    this.docPreviewSrc = `data:${mime};base64,${base64}`
                    return
                }

                if (resolvedUrl) {
                    this.docPreviewSrc = resolvedUrl
                    return
                }

                this.$refs.docPreviewModal.hide()
                this.showAttachmentUnavailableToast()
            } catch (e) {
                const url = this.docPreviewDownloadUrl
                    || await reimbursementService.resolveAttachmentUrl(filePath)
                if (url) {
                    this.docPreviewSrc = url
                } else {
                    this.$refs.docPreviewModal.hide()
                    this.showAttachmentUnavailableToast()
                }
            } finally {
                this.docPreviewLoading = false
            }
        },

        downloadDocPreview() {
            const url = this.docPreviewDownloadUrl || this.docPreviewSrc
            if (!url) return
            if (String(url).startsWith('data:')) {
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', this.docPreviewFileName || 'document')
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                return
            }
            window.open(url, '_blank', 'noopener')
        },

        resetDocPreview() {
            this.docPreviewLoading = false
            this.docPreviewKind = null
            this.docPreviewSrc = null
            this.docPreviewDownloadUrl = null
            this.docPreviewFileName = null
            this.docPreviewTitle = 'Document preview'
        },

        showAttachmentUnavailableToast() {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: 'Attachment unavailable',
                    text: 'Could not open the supporting document. It may not have been uploaded yet, or the download service is unavailable.',
                    icon: 'AlertCircleIcon',
                    variant: 'warning',
                },
            })
        },

        resolveInitialViewMode() {

            const raw = this.$route.query.tab || this.$route.query.view

            const requested = typeof raw === 'string' ? raw.toLowerCase() : ''

            if (requested === 'mine' || requested === 'my') return 'mine'

            if (requested === 'approvals' && this.showApprovalsTab) return 'approvals'

            if (requested === 'all' && this.showAllTab) return 'all'

            if (this.showAllTab) return 'all'

            return 'mine'

        },

        syncViewQuery(mode) {

            const current = this.$route.query.tab || this.$route.query.view

            if (current === mode) return

            this.$router.replace({

                name: this.$route.name,

                query: { ...this.$route.query, tab: mode },

            }).catch(() => {})

        },

        switchView(mode) {

            if (this.viewMode === mode) return

            this.viewMode = mode

            this.filter = ''

            this.currentPage = 1

            this.syncViewQuery(mode)

            this.loadList()

        },

        buildListFilters() {

            return {

                dateFilterSelected: this.searchFilters.dateFieldSelected || REIMBURSEMENT_DATE_FILTER_FIELD,

                start_date: this.searchFilters.startDate || '',

                end_date: this.searchFilters.endDate || '',

            }

        },

        applyDateFilter() {

            this.currentPage = 1

            this.loadList()

        },

        resetDateFilter() {

            const defaultCycle = getDefaultReimbursementCycleDates()

            this.searchFilters.startDate = defaultCycle.startDate

            this.searchFilters.endDate = defaultCycle.endDate

            this.applyDateFilter()

        },

        escapeCsvValue(val) {

            const s = val === null || val === undefined ? '' : String(val)

            if (s.includes(',') || s.includes('"') || s.includes('\n')) {

                return `"${s.replace(/"/g, '""')}"`

            }

            return s

        },

        buildReimbursementExportRows(items) {

            return items.map(item => [

                item.id ?? '',

                this.submittedByName(item),

                this.employeeName(item),

                item.expense_date || '',

                item.item || '',

                item.account || '',

                item.vertical || '',

                item.category || '',

                item.sub_category || '',

                item.amount ?? '',

                item.payment_mode || '',

                item.location || '',

                this.statusLabel(item),

                item.invoice_no || '',

                item.is_gst ? 'Yes' : 'No',

                item.gst_number || item.gstNumber || '',

                item.is_tds ? 'Yes' : 'No',

                item.tds_amount ?? '',

                item.comment || '',

            ])

        },

        downloadReimbursementsCsv() {

            if (!this.canDownload) return

            const items = this.displayItems

            if (!items.length) {

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: 'No reimbursements to download for the current filters.',

                        icon: 'AlertTriangleIcon',

                        variant: 'warning',

                        timeout: 4000,

                    },

                })

                return

            }

            this.downloadInProgress = true

            try {

                const headers = [

                    'ID',

                    'Submitted By',

                    'Employee',

                    'Expense Date',

                    'Item',

                    'Account',

                    'Vertical',

                    'Category',

                    'Sub-Category',

                    'Amount',

                    'Payment Mode',

                    'Location',

                    'Status',

                    'Invoice No',

                    'GST',

                    'GST Number',

                    'TDS',

                    'TDS Amount',

                    'Comment',

                ]

                const rows = this.buildReimbursementExportRows(items)

                const csv = [

                    headers.map(h => this.escapeCsvValue(h)).join(','),

                    ...rows.map(row => row.map(cell => this.escapeCsvValue(cell)).join(',')),

                ].join('\n')

                const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })

                const url = URL.createObjectURL(blob)

                const link = document.createElement('a')

                const start = this.searchFilters.startDate || 'all'

                const end = this.searchFilters.endDate || 'all'

                link.setAttribute('href', url)

                link.setAttribute('download', `reimbursements_${this.viewMode}_${start}_${end}.csv`)

                document.body.appendChild(link)

                link.click()

                document.body.removeChild(link)

                URL.revokeObjectURL(url)

            } finally {

                this.downloadInProgress = false

            }

        },

        async fetchApprovalQueueData() {
            const canFn = this.$can.bind(this)
            const filters = this.buildListFilters()
            const queueRes = await reimbursementService.getApprovalQueue(filters)

            if (queueRes.status === 'OK') {
                return filterApprovalQueue(canFn, queueRes.data || [], this.userData)
            }

            return []
        },

        async loadList() {

            this.loading = true

            try {
                let res
                const filters = this.buildListFilters()

                if (this.viewMode === 'approvals') {
                    const queue = await this.fetchApprovalQueueData()
                    res = { status: 'OK', data: queue }
                } else if (this.viewMode === 'all') {
                    res = await reimbursementService.getAllReimbursementsAdmin(filters)
                } else {
                    res = await reimbursementService.getMyReimbursements(filters)
                }

                if (res.status === 'OK') {

                    this.reimbursementList = (res.data || []).map(row => this.decorateListRow(row))

                    this.totalRows = this.displayItems.length

                    if (this.viewMode === 'approvals') {
                        this.approvalQueueList = this.reimbursementList
                    }

                } else if (res.status === 'SERVICE_UNAVAILABLE') {

                    this.reimbursementList = []

                    this.totalRows = 0

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: res.message || 'Reimbursement service unavailable. Please contact admin.',

                            icon: 'AlertTriangleIcon',

                            variant: 'warning',

                            timeout: 6000,

                        },

                    })

                } else if ((res.status === 401) || (res.status === 403)) {

                    window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])

                    localStorage.clear()

                    this.$router.push({ name: 'auth-login' })

                }
            } catch (err) {

                this.reimbursementList = []

                this.totalRows = 0

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: 'Reimbursement service unavailable. Please contact admin.',

                        icon: 'AlertTriangleIcon',

                        variant: 'warning',

                        timeout: 6000,

                    },

                })

            } finally {

                this.loading = false

            }

        },

        canActOnItem(item) {

            return canApproveAtStage(this.$can.bind(this), item, this.userData)

        },

        canEditItem(item) {

            return canEditReimbursement(this.$can.bind(this), this.userData, item)

        },

        canCancelItem(item) {

            return canCancelOwnReimbursement(this.userData, item)

        },

        editItem(item) {

            const denialReason = getEditDenialReason(this.$can.bind(this), this.userData, item)

            if (denialReason) {

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: denialReason,

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 6000,

                    },

                })

                return

            }

            this.$router.push({ name: 'apps-edit-reimbursement', params: { id: item.id } })

        },

        confirmCancel(item) {

            const denialReason = getCancelDenialReason(this.userData, item)

            if (denialReason) {

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: denialReason,

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 6000,

                    },

                })

                return

            }

            this.cancelTarget = item

            this.$refs.cancelModal.show()

        },

        submitCancel(bvModalEvt) {

            bvModalEvt.preventDefault()

            if (!this.cancelTarget) return

            const denialReason = getCancelDenialReason(this.userData, this.cancelTarget)

            if (denialReason) {

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: denialReason,

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 6000,

                    },

                })

                return

            }

            const withdrawnRow = this.cancelTarget

            const id = withdrawnRow.id

            this.actionInProgress = id

            reimbursementService.cancelReimbursement(id).then(async res => {

                this.actionInProgress = null

                if (res.status === 'OK') {

                    this.reimbursementList = this.reimbursementList.filter(row => row.id !== id)

                    this.totalRows = this.displayItems.length

                    reimbursementService.notifyReimbursementEmails('withdrawn', withdrawnRow).catch(() => {})

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: 'Reimbursement withdrawn',

                            icon: 'CheckIcon',

                            variant: 'success',

                            timeout: 4000,

                        },

                    })

                    this.$refs.cancelModal.hide()

                    this.$refs.viewDetailsModal.hide()

                    await this.loadList()

                } else {

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: formatReimbursementApiError(res, 'Withdraw failed'),

                            icon: 'AlertTriangleIcon',

                            variant: 'danger',

                            timeout: 6000,

                        },

                    })

                }

            }).catch(err => {

                this.actionInProgress = null

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: formatReimbursementApiError(err?.response?.data, 'Could not withdraw reimbursement. Please try again.'),

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 6000,

                    },

                })

            })

        },

        safeParseJSON(value) {

            if (!value) return {}

            if (typeof value === 'object') return value

            try {

                return JSON.parse(value)

            } catch (e) {

                return {}

            }

        },

        parsedName(value) {

            const parsed = this.safeParseJSON(value)

            return parsed.title || '-'

        },

        decorateListRow(row) {

            if (!row || typeof row !== 'object') return row

            const submitter = this.submittedByName(row)

            const employee = this.employeeName(row)

            const onBehalf = this.onBehalfName(row)

            const rawEmployee = row.employee

            return {

                ...row,

                employee: (rawEmployee && typeof rawEmployee === 'object') ? rawEmployee : employee,

                employee_display: employee,

                submitted_by_search: [submitter, employee, onBehalf].filter(name => name && name !== '-').join(' '),

                status_label: formatReimbursementStatusLabel(row),

            }

        },

        submittedByName(item) {

            return getReimbursementSubmitterName(item) || this.parsedName(item && item.added_by)

        },

        employeeName(item) {
            return getReimbursementEmployeeName(item) || this.submittedByName(item)

        },

        onBehalfName(item) {

            return getReimbursementOnBehalfName(item)

        },

        claimPartyLabel(item) {

            const submitter = this.submittedByName(item)

            const onBehalf = this.onBehalfName(item)

            if (onBehalf) return `${submitter} (on behalf of ${onBehalf})`

            return submitter

        },

        formatDateTime(value) {

            if (!value) return '-'

            try {

                return new Date(value).toLocaleString()

            } catch (e) {

                return value

            }

        },

        statusLabel(item) {

            return deriveReimbursementStatus(item)

        },

        trailLabel(item, stageStatus) {

            return formatReimbursementTrailLabel(item, stageStatus)

        },

        rejectedStageLabel(item) {

            if (!item) return 'Rejected'

            return formatReimbursementStageOutcome(
                item.rejected_stage || item.rejectedStage,
                'rejected',
            )

        },

        statusVariant(item) {

            return deriveReimbursementStatusVariant(item)

        },

        async viewDetails(item) {

            this.selectedItem = item

            this.$refs.viewDetailsModal.show()

            try {
                const res = await reimbursementService.getReimbursementById(item.id)
                if (res.status === 'OK' && res.data) {
                    this.selectedItem = { ...item, ...res.data }
                }
            } catch (e) {
                // Keep list row if detail fetch fails
            }

        },

        confirmApprove(item) {

            this.approveTarget = item

            this.approveComment = ''

            this.approveCommentError = false

            this.$refs.approveModal.show()

        },

        openRejectModal(item) {

            this.rejectTarget = item

            this.rejectComment = ''

            this.rejectCommentError = false

            this.$refs.rejectModal.show()

        },

        requiredDecisionComment(value) {
            return String(value || '').trim()
        },

        blockIfCommentMissing(comment, errorKey) {
            if (comment) return false
            this[errorKey] = true
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: 'Comment is required',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                    timeout: 5000,
                },
            })
            return true
        },

        applyReimbursementRowUpdate(apiResponse, previousStatus, action = 'approve') {
            const updated = extractReimbursementRow(apiResponse)
            if (!updated?.id) return

            const index = this.reimbursementList.findIndex(row => row.id === updated.id)
            const merged = this.decorateListRow({
                ...(index >= 0 ? this.reimbursementList[index] : {}),
                ...updated,
            })

            const priorStatus = normalizeReimbursementStatus(
                previousStatus != null ? { status: previousStatus } : (index >= 0 ? this.reimbursementList[index] : null),
            )
            if (priorStatus && (!updated.status || updated.status === priorStatus)) {
                if (action === 'reject') {
                    merged.status = REIMBURSEMENT_STATUSES.REJECTED
                } else {
                    merged.status = NEXT_STATUS_AFTER_APPROVAL[priorStatus] || merged.status
                }
            }
            merged.status_label = formatReimbursementStatusLabel(merged)

            if (index >= 0) {
                this.$set(this.reimbursementList, index, merged)
            }

            if (this.selectedItem?.id === updated.id) {
                this.selectedItem = merged
            }

            if (this.viewMode === 'approvals') {
                this.approvalQueueList = filterApprovalQueue(
                    this.$can.bind(this),
                    this.reimbursementList,
                    this.userData,
                )
                this.reimbursementList = this.approvalQueueList
            }
        },

        submitApprove(bvModalEvt) {

            bvModalEvt.preventDefault()

            if (!this.approveTarget) return

            const comment = this.requiredDecisionComment(this.approveComment)
            if (this.blockIfCommentMissing(comment, 'approveCommentError')) return

            const denialReason = getApproveDenialReason(
                this.$can.bind(this),
                this.approveTarget,
                this.userData,
            )
            if (denialReason) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: denialReason,
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                        timeout: 8000,
                    },
                })
                return
            }

            const id = this.approveTarget.id
            const previousStatus = normalizeReimbursementStatus(this.approveTarget)

            this.actionInProgress = id

            reimbursementService.approveReimbursement(id, { comment }).then(async res => {

                this.actionInProgress = null

                if (res.status === 'OK') {

                    this.applyReimbursementRowUpdate(res, previousStatus)

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: 'Reimbursement approved',

                            icon: 'CheckIcon',

                            variant: 'success',

                            timeout: 4000,

                        },

                    })

                    reimbursementService.notifyReimbursementEmails('approved', res.data, {
                        previousStatus,
                        approverComment: comment,
                    }).then(({ failures }) => {
                        if (failures?.length) {
                            this.$toast({
                                component: ToastificationContent,
                                props: {
                                    title: 'Notification email could not be sent.',
                                    icon: 'AlertTriangleIcon',
                                    variant: 'warning',
                                    timeout: 4000,
                                },
                            })
                        }
                    }).catch(err => {
                        console.warn('[reimbursement] email notification error:', err)
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: 'Notification email could not be sent.',
                                icon: 'AlertTriangleIcon',
                                variant: 'warning',
                                timeout: 4000,
                            },
                        })
                    })

                    this.$refs.approveModal.hide()

                    this.$refs.viewDetailsModal.hide()

                    await this.loadList()

                } else {

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: formatReimbursementApiError(res, 'Approval failed'),

                            icon: 'AlertTriangleIcon',

                            variant: 'danger',

                            timeout: 6000,

                        },

                    })

                }

            }).catch(err => {

                this.actionInProgress = null

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: formatReimbursementApiError(err?.response?.data, 'Could not complete approval. Please try again.'),

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 6000,

                    },

                })

            })

        },

        submitReject(bvModalEvt) {

            bvModalEvt.preventDefault()

            if (!this.rejectTarget) return

            const comment = this.requiredDecisionComment(this.rejectComment)
            if (this.blockIfCommentMissing(comment, 'rejectCommentError')) return

            const denialReason = getApproveDenialReason(
                this.$can.bind(this),
                this.rejectTarget,
                this.userData,
            )
            if (denialReason) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: denialReason,
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                        timeout: 8000,
                    },
                })
                return
            }

            const id = this.rejectTarget.id
            const previousStatus = normalizeReimbursementStatus(this.rejectTarget)

            this.actionInProgress = id

            reimbursementService.rejectReimbursement(id, { comment }).then(async res => {

                this.actionInProgress = null

                if (res.status === 'OK') {

                    this.applyReimbursementRowUpdate(res, previousStatus, 'reject')

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: 'Reimbursement rejected',

                            icon: 'XIcon',

                            variant: 'warning',

                            timeout: 4000,

                        },

                    })

                    reimbursementService.notifyReimbursementEmails('rejected', res.data, {
                        rejectedStage: previousStatus,
                        approverComment: comment,
                    }).then(({ failures }) => {
                        if (failures?.length) {
                            this.$toast({
                                component: ToastificationContent,
                                props: {
                                    title: 'Notification email could not be sent.',
                                    icon: 'AlertTriangleIcon',
                                    variant: 'warning',
                                    timeout: 4000,
                                },
                            })
                        }
                    }).catch(err => {
                        console.warn('[reimbursement] email notification error:', err)
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: 'Notification email could not be sent.',
                                icon: 'AlertTriangleIcon',
                                variant: 'warning',
                                timeout: 4000,
                            },
                        })
                    })

                    this.$refs.rejectModal.hide()

                    this.$refs.viewDetailsModal.hide()

                    await this.loadList()

                } else {

                    this.$toast({

                        component: ToastificationContent,

                        props: {

                            title: formatReimbursementApiError(res, 'Rejection failed'),

                            icon: 'AlertTriangleIcon',

                            variant: 'danger',

                            timeout: 5000,

                        },

                    })

                }

            }).catch(err => {

                this.actionInProgress = null

                this.$toast({

                    component: ToastificationContent,

                    props: {

                        title: formatReimbursementApiError(err?.response?.data, 'Could not complete rejection. Please try again.'),

                        icon: 'AlertTriangleIcon',

                        variant: 'danger',

                        timeout: 5000,

                    },

                })

            })

        },

        onFiltered(filteredItems) {

            this.totalRows = filteredItems.length

            this.currentPage = 1

        },

    },

}

</script>

<style lang="scss">

@import '@core/scss/vue/libs/vue-flatpicker.scss';

@import '@core/scss/vue/apps/report-tracker.scss';

</style>



<style scoped>

.reimbursement-list-page {
    margin-top: -0.85rem;
}

.reimbursement-list-content {
    background: transparent;
}

/* Flatten shared report-tracker surfaces for reimbursement */
.reimbursement-list-page ::v-deep .report-toolbar-body {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0.375rem 0;
}

.reimbursement-list-page ::v-deep .report-toolbar-body--filters-open {
    border-bottom: none;
    margin-bottom: 0;
}

.reimbursement-list-page ::v-deep .report-toolbar-context {
    background: transparent;
    border: 1px solid rgba(34, 41, 47, 0.08);
    padding: 0.35rem 0.7rem;
    font-size: 1rem;
    color: #4b465c;
}

.reimbursement-list-page ::v-deep .report-context-text {
    font-size: 1rem;
    color: #4b465c;
}

.reimbursement-list-page ::v-deep .reimbursement-table-wrap {
    background: #fff;
    box-shadow: none;
    border: 1px solid rgba(34, 41, 47, 0.1);
    border-radius: 0.428rem;
    max-height: none;
    overflow-x: auto;
    overflow-y: visible;

    .table-responsive {
        margin-bottom: 0;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }
}

.reimbursement-list-page ::v-deep .reimbursement-table {
    font-size: 1.0625rem;
    color: #4b465c;

    thead th {
        position: static;
        box-shadow: none;
        font-size: 1.125rem;
        font-weight: 600;
        color: #2f2b3d !important;
    }

    tbody td {
        vertical-align: middle;
        font-size: 1.0625rem;
        color: #4b465c;
        padding-top: 0.7rem;
        padding-bottom: 0.7rem;
    }

    .badge {
        font-size: 0.9375rem;
        font-weight: 500;
        padding: 0.4em 0.7em;
        line-height: 1.3;
        white-space: nowrap;
    }

    .small,
    small,
    .reimb-subcategory,
    .reimb-on-behalf {
        font-size: 0.875rem;
        color: #5e5873;
    }
}

.reimb-item-cell {
    max-width: 220px;
}

.reimbursement-list-page ::v-deep .report-filter-panel {
    padding: 0.5rem 0.625rem 0.625rem;
    background: transparent;
    border: 1px solid rgba(34, 41, 47, 0.08);
    border-radius: 0.428rem;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
    box-shadow: none;
}

.reimbursement-list-page ::v-deep .report-filter-panel-divider {
    margin: 0.5rem 0 0.625rem;
}

.reimbursement-list-page ::v-deep .report-section-heading {
    margin: 0 0 0.4rem;
    padding-bottom: 0.3rem;
    font-size: 0.75rem;
}

.reimbursement-list-page ::v-deep .report-filter-grid {
    gap: 0.45rem 0.625rem;
}

.reimbursement-list-page ::v-deep .report-filter-group {
    gap: 0.25rem;
}

.reimbursement-view-tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0;
    margin-bottom: 0.625rem;
    background: transparent;
    border: none;
    box-shadow: none;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;
}

.reimbursement-tab-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.85rem;
    font-size: 1rem;
    font-weight: 500;
    color: #4b465c;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.35rem;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

    &:hover {
        color: #00ac99;
        background: rgba(0, 172, 153, 0.06);
        border-color: rgba(0, 172, 153, 0.12);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 172, 153, 0.2);
    }

    &--active {
        color: #00ac99;
        background: rgba(0, 172, 153, 0.1);
        border-color: rgba(0, 172, 153, 0.22);
        box-shadow: none;

        &:hover {
            color: #00ac99;
            background: rgba(0, 172, 153, 0.14);
        }
    }
}

.reimbursement-tab-badge {
    font-size: 0.875rem;
    line-height: 1.2;
    margin-left: 0.15rem;
}

.reimbursement-toolbar-context {
    flex: 1 1 auto;
    min-width: 0;
}

.reimbursement-toolbar-search {
    width: 220px;
    max-width: 100%;
}

.reimbursement-list-page ::v-deep .reimbursement-search-group {
    .input-group-text {
        background: #fff;
        border-color: rgba(34, 41, 47, 0.12);
        color: #5e5873;
        padding: 0.25rem 0.5rem;
    }

    .form-control {
        font-size: 0.9375rem;
        height: calc(1.5em + 0.5rem + 2px);
        min-height: 31px;
        border-color: rgba(34, 41, 47, 0.12);
    }
}

.reimb-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: calc(1.5em + 0.5rem + 2px);
    height: calc(1.5em + 0.5rem + 2px);
    min-width: 31px;
    min-height: 31px;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #5e5873;
    background: #f8f8f8;
    border: 1px solid rgba(130, 134, 139, 0.45);
    border-radius: 0.428rem;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
        color: #00ac99;
        border-color: rgba(0, 172, 153, 0.35);
        background: rgba(0, 172, 153, 0.08);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 172, 153, 0.2);
    }

    &:disabled,
    &[disabled] {
        opacity: 0.55;
        cursor: pointer;
        pointer-events: none;
    }
}

.reimb-filter-toggle {
    width: calc(1.5em + 0.5rem + 2px);
    min-width: 31px;
    padding: 0;
    justify-content: center;
}

.reimbursement-raise-link {
    display: inline-flex;

    &:hover {
        text-decoration: none;
    }
}

.reimbursement-filter-grid {
    margin-bottom: 0;
}

.reimbursement-filter-actions {
    justify-content: flex-end;
}

.reimbursement-filter-action-btns {
    gap: 0.35rem;
}

.reimbursement-empty-state {
    border: none;
    background: transparent;
    padding: 1.5rem 1rem;
}

.reimbursement-list-page ::v-deep .reimbursement-table tbody > tr.b-table-busy-slot > td {
    padding: 0.5rem 0.75rem;
}

.reimbursement-list-page ::v-deep .reimbursement-table tbody > tr.b-table-empty-row > td {
    padding: 1.5rem 0.75rem;
    border-bottom: none;
}

.reimbursement-pagination-footer {
    margin-top: 0.75rem;
    padding: 0.5rem 0.25rem 0;
    border-top: none;
}

.reimbursement-page-summary {
    font-size: 1rem;
    color: #4b465c;
}

.reimbursement-pagination {
    margin-left: auto;
}

.reimbursement-list-page ::v-deep .reimbursement-pagination {
    .page-link {
        font-size: 1rem;
        min-width: 2.25rem;
        line-height: 1.4;
    }
}

.reimbursement-actions-dropdown ::v-deep .reimb-actions-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.35rem;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(34, 41, 47, 0.06);
    }
}

::v-deep #reimbursement-view-details-modal .modal-body {
    word-break: break-word;
}

.reimbursement-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
}

.reimbursement-doc-preview-frame {
    min-height: 70vh;
    height: 70vh;
    border: 0;
    background: #f8f8f8;
}

.reimbursement-doc-preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
}

@media (max-width: 575.98px) {
    .reimbursement-view-tabs {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .reimbursement-tab-pill {
        width: 100%;
        justify-content: center;
    }

    .reimbursement-pagination-footer {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;
    }

    .reimbursement-pagination {
        margin-left: 0;
        justify-content: center;
    }

    .reimbursement-toolbar-search {
        width: 100%;
    }
}

</style>


